/** 레거시 `maxMatchOperatingHoursMapper.js` 포팅 */

export const SCREEN_TYPE = {
  DETAIL: 'DETAIL',
  NEW_CLINIC: 'NEW_CLINIC',
  LIGHT_WEB: 'LIGHT_WEB',
} as const;

export type ScreenType = (typeof SCREEN_TYPE)[keyof typeof SCREEN_TYPE];

export const SCREEN_SECTION_KEY: Record<ScreenType, keyof ScreenSettings> = {
  [SCREEN_TYPE.DETAIL]: 'detailSettings',
  [SCREEN_TYPE.NEW_CLINIC]: 'newClinic',
  [SCREEN_TYPE.LIGHT_WEB]: 'lightWeb',
};

const DAY_TO_API: Record<number, string> = {
  1: 'MONDAY',
  2: 'TUESDAY',
  3: 'WEDNESDAY',
  4: 'THURSDAY',
  5: 'FRIDAY',
  6: 'SATURDAY',
  7: 'SUNDAY',
};

const DAY_FROM_API: Record<string, number> = {
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
  SUNDAY: 7,
};

export interface ScreenSection {
  startTime: string;
  endTime: string;
  days: number[];
}

export interface ScreenSettings {
  detailSettings: ScreenSection;
  newClinic: ScreenSection;
  lightWeb: ScreenSection;
}

export interface ServerOperation {
  available: boolean;
  startTime: string;
  endTime: string;
  days: number[];
  timezone: string;
}

export const DEFAULT_SCREEN_SECTION: ScreenSection = {
  startTime: '09:00',
  endTime: '18:00',
  days: [1, 2, 3, 4, 5],
};

export const DEFAULT_SCREEN_SETTINGS: ScreenSettings = {
  detailSettings: { ...DEFAULT_SCREEN_SECTION },
  newClinic: { ...DEFAULT_SCREEN_SECTION },
  lightWeb: { ...DEFAULT_SCREEN_SECTION },
};

export const INITIAL_SCREEN_SETTINGS: ScreenSettings = {
  detailSettings: { ...DEFAULT_SCREEN_SECTION, days: [] },
  newClinic: { ...DEFAULT_SCREEN_SECTION, days: [] },
  lightWeb: { ...DEFAULT_SCREEN_SECTION, days: [] },
};

export const DEFAULT_SERVER_OPERATION: ServerOperation = {
  available: false,
  startTime: '09:00',
  endTime: '18:00',
  days: [],
  timezone: 'Asia/Seoul',
};

export const SCREEN_SECTION_LABEL: Record<keyof ScreenSettings, string> = {
  detailSettings: '맥스매치 상세 설정',
  newClinic: '맥스매치 신규 병의원',
  lightWeb: '맥스매치 Lite 웹',
};

export const MAX_MATCH_OPERATING_WINDOW_ERROR_MESSAGE =
  '화면 운영시간과 운영요일은 ECS 운영 범위 내에서만 설정할 수 있습니다.';

export const MAX_MATCH_OPERATING_HOUR_UNAVAILABLE_MESSAGE =
  'ECS 운영시간을 조회할 수 없어 화면 운영시간을 수정할 수 없습니다.';

export const INVALID_OPERATING_WINDOW_MESSAGE =
  '시작 시각은 종료 시각보다 빨라야 하며 다음 요일로 넘길 수 없습니다.';

export const MAX_MATCH_OPERATING_SAVE_FAILED_MESSAGE =
  '운영 시간 설정 저장에 실패했습니다.';

/**
 * 레거시 저장 catch — API code별 한글 폴백.
 * message가 있으면 서버 메시지를 우선한다.
 */
export const getOperatingHoursSaveErrorMessage = (error: unknown): string => {
  const data = (
    error as { response?: { data?: { code?: string; message?: string } } }
  )?.response?.data;
  const code = data?.code;
  const message = data?.message?.trim();

  if (code === 'SCREEN_WINDOW_OUTSIDE_OPERATING_RANGE') {
    return message || MAX_MATCH_OPERATING_WINDOW_ERROR_MESSAGE;
  }
  if (code === 'MAX_MATCH_OPERATING_HOUR_UNAVAILABLE') {
    return message || MAX_MATCH_OPERATING_HOUR_UNAVAILABLE_MESSAGE;
  }
  if (code === 'INVALID_OPERATING_WINDOW') {
    return message || INVALID_OPERATING_WINDOW_MESSAGE;
  }
  return message || MAX_MATCH_OPERATING_SAVE_FAILED_MESSAGE;
};

export const WEEKDAYS = [
  { value: 1, label: '월' },
  { value: 2, label: '화' },
  { value: 3, label: '수' },
  { value: 4, label: '목' },
  { value: 5, label: '금' },
  { value: 6, label: '토' },
  { value: 7, label: '일' },
] as const;

const fromApiTime = (time?: string | null): string | null => {
  if (!time) return null;
  return time.slice(0, 5);
};

const toApiTime = (time?: string | null): string | null => {
  if (!time) return null;
  return time.length === 5 ? `${time}:00` : time;
};

const mapDaysFromApi = (daysOfWeek: string[] = []): number[] =>
  [...daysOfWeek]
    .map((day) => DAY_FROM_API[day])
    .filter(Boolean)
    .sort((a, b) => a - b);

const mapDaysToApi = (days: number[] = []): string[] =>
  [...days].map((day) => DAY_TO_API[day]).filter(Boolean);

const mapScreenWindow = (
  window: { startTime?: string; endTime?: string; daysOfWeek?: string[] } | undefined,
  fallback: ScreenSection,
): ScreenSection => ({
  startTime: fromApiTime(window?.startTime) ?? fallback.startTime,
  endTime: fromApiTime(window?.endTime) ?? fallback.endTime,
  days:
    Array.isArray(window?.daysOfWeek) && window.daysOfWeek.length > 0
      ? mapDaysFromApi(window.daysOfWeek)
      : [...fallback.days],
});

export const mapOperatingHoursResponse = (
  response?: {
    available?: boolean;
    startTime?: string;
    endTime?: string;
    daysOfWeek?: string[];
    timezone?: string;
  } | null,
): ServerOperation => ({
  available: Boolean(response?.available),
  startTime:
    fromApiTime(response?.startTime) ?? DEFAULT_SERVER_OPERATION.startTime,
  endTime: fromApiTime(response?.endTime) ?? DEFAULT_SERVER_OPERATION.endTime,
  days: mapDaysFromApi(response?.daysOfWeek),
  timezone: response?.timezone ?? DEFAULT_SERVER_OPERATION.timezone,
});

export const mapScreenWindowsResponse = (
  windows: Array<{ screenType: string; startTime?: string; endTime?: string; daysOfWeek?: string[] }> = [],
): ScreenSettings => {
  const byType = Object.fromEntries(
    windows.map((window) => [window.screenType, window]),
  );

  return {
    detailSettings: mapScreenWindow(
      byType[SCREEN_TYPE.DETAIL],
      DEFAULT_SCREEN_SETTINGS.detailSettings,
    ),
    newClinic: mapScreenWindow(
      byType[SCREEN_TYPE.NEW_CLINIC],
      DEFAULT_SCREEN_SETTINGS.newClinic,
    ),
    lightWeb: mapScreenWindow(
      byType[SCREEN_TYPE.LIGHT_WEB],
      DEFAULT_SCREEN_SETTINGS.lightWeb,
    ),
  };
};

export const toScreenWindowUpsertPayload = (section: ScreenSection) => ({
  startTime: toApiTime(section.startTime),
  endTime: toApiTime(section.endTime),
  daysOfWeek: mapDaysToApi(section.days),
});

export const normalizeScreenSettings = (settings?: Partial<ScreenSettings>): ScreenSettings => ({
  detailSettings: {
    startTime:
      settings?.detailSettings?.startTime ??
      DEFAULT_SCREEN_SETTINGS.detailSettings.startTime,
    endTime:
      settings?.detailSettings?.endTime ??
      DEFAULT_SCREEN_SETTINGS.detailSettings.endTime,
    days: Array.isArray(settings?.detailSettings?.days)
      ? [...settings.detailSettings.days].sort((a, b) => a - b)
      : [...DEFAULT_SCREEN_SETTINGS.detailSettings.days],
  },
  newClinic: {
    startTime:
      settings?.newClinic?.startTime ?? DEFAULT_SCREEN_SETTINGS.newClinic.startTime,
    endTime:
      settings?.newClinic?.endTime ?? DEFAULT_SCREEN_SETTINGS.newClinic.endTime,
    days: Array.isArray(settings?.newClinic?.days)
      ? [...settings.newClinic.days].sort((a, b) => a - b)
      : [...DEFAULT_SCREEN_SETTINGS.newClinic.days],
  },
  lightWeb: {
    startTime:
      settings?.lightWeb?.startTime ?? DEFAULT_SCREEN_SETTINGS.lightWeb.startTime,
    endTime:
      settings?.lightWeb?.endTime ?? DEFAULT_SCREEN_SETTINGS.lightWeb.endTime,
    days: Array.isArray(settings?.lightWeb?.days)
      ? [...settings.lightWeb.days].sort((a, b) => a - b)
      : [...DEFAULT_SCREEN_SETTINGS.lightWeb.days],
  },
});

const timeToMinutes = (time?: string | null): number | null => {
  if (!time) return null;
  const [hours, minutes] = time.split(':').map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  return hours * 60 + minutes;
};

export const validateScreenSectionWithinServerOperation = (
  section: ScreenSection,
  serverOperation: ServerOperation,
): { valid: boolean; message?: string } => {
  const { startTime, endTime, days } = section;
  const startMinutes = timeToMinutes(startTime);
  const endMinutes = timeToMinutes(endTime);
  const serverStartMinutes = timeToMinutes(serverOperation.startTime);
  const serverEndMinutes = timeToMinutes(serverOperation.endTime);

  if (!startTime || !endTime || startMinutes == null || endMinutes == null) {
    return { valid: false, message: INVALID_OPERATING_WINDOW_MESSAGE };
  }
  if (startMinutes >= endMinutes) {
    return { valid: false, message: INVALID_OPERATING_WINDOW_MESSAGE };
  }
  if (
    !serverOperation.available ||
    serverStartMinutes == null ||
    serverEndMinutes == null ||
    serverOperation.days.length === 0
  ) {
    return { valid: false, message: MAX_MATCH_OPERATING_HOUR_UNAVAILABLE_MESSAGE };
  }
  if (startMinutes < serverStartMinutes || endMinutes > serverEndMinutes) {
    return { valid: false, message: MAX_MATCH_OPERATING_WINDOW_ERROR_MESSAGE };
  }
  if (days.length === 0 || days.some((day) => !serverOperation.days.includes(day))) {
    return { valid: false, message: MAX_MATCH_OPERATING_WINDOW_ERROR_MESSAGE };
  }
  return { valid: true };
};

export const getChangedScreenTypes = (
  current: ScreenSettings,
  initial: ScreenSettings,
): ScreenType[] => {
  const normalizedCurrent = normalizeScreenSettings(current);
  const normalizedInitial = normalizeScreenSettings(initial);

  return Object.values(SCREEN_TYPE).filter((screenType) => {
    const sectionKey = SCREEN_SECTION_KEY[screenType];
    return (
      JSON.stringify(normalizedCurrent[sectionKey]) !==
      JSON.stringify(normalizedInitial[sectionKey])
    );
  });
};

export const validateChangedScreenSettings = (
  screenSettings: ScreenSettings,
  initialScreenSettings: ScreenSettings,
  serverOperation: ServerOperation,
): { valid: boolean; message?: string } => {
  const changedScreenTypes = getChangedScreenTypes(
    screenSettings,
    initialScreenSettings,
  );
  const normalized = normalizeScreenSettings(screenSettings);

  for (const screenType of changedScreenTypes) {
    const sectionKey = SCREEN_SECTION_KEY[screenType];
    const result = validateScreenSectionWithinServerOperation(
      normalized[sectionKey],
      serverOperation,
    );
    if (!result.valid) return result;
  }
  return { valid: true };
};

/** 서버 운용 시각 범위 안으로 화면 구간 시각을 클램프한다. */
const clampSectionTimeToServerRange = (
  section: ScreenSection,
  serverOperation: ServerOperation,
): ScreenSection => {
  const serverStartMinutes = timeToMinutes(serverOperation.startTime);
  const serverEndMinutes = timeToMinutes(serverOperation.endTime);
  const startMinutes = timeToMinutes(section.startTime);
  const endMinutes = timeToMinutes(section.endTime);

  if (
    serverStartMinutes == null ||
    serverEndMinutes == null ||
    startMinutes == null ||
    endMinutes == null
  ) {
    return section;
  }

  const clampedStartMinutes = Math.max(startMinutes, serverStartMinutes);
  const clampedEndMinutes = Math.min(endMinutes, serverEndMinutes);

  if (clampedStartMinutes >= clampedEndMinutes) {
    return {
      ...section,
      startTime: serverOperation.startTime,
      endTime: serverOperation.endTime,
    };
  }

  const toTime = (minutes: number) =>
    `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

  return {
    ...section,
    startTime: toTime(clampedStartMinutes),
    endTime: toTime(clampedEndMinutes),
  };
};

/**
 * 로드·동기화 시 화면 운영 설정을 ECS 서버 운용 범위로 맞춘다.
 * 요일 필터 + 시작/종료 시각 clamp (admin-ruda-web `6553a8f`).
 */
export const clampScreenSettingsToServerOperation = (
  settings: ScreenSettings,
  serverOperation: ServerOperation,
): ScreenSettings => {
  const allowed = new Set(serverOperation.days);
  const clampDays = (section: ScreenSection): ScreenSection => ({
    ...section,
    days:
      allowed.size === 0
        ? section.days
        : section.days.filter((day) => allowed.has(day)),
  });

  const byDays: ScreenSettings = {
    detailSettings: clampDays(settings.detailSettings),
    newClinic: clampDays(settings.newClinic),
    lightWeb: clampDays(settings.lightWeb),
  };

  if (!serverOperation.startTime || !serverOperation.endTime) {
    return byDays;
  }

  return {
    detailSettings: clampSectionTimeToServerRange(
      byDays.detailSettings,
      serverOperation,
    ),
    newClinic: clampSectionTimeToServerRange(byDays.newClinic, serverOperation),
    lightWeb: clampSectionTimeToServerRange(byDays.lightWeb, serverOperation),
  };
};
